import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Metadata from '../../components/Metadata/Metadata';
import Section from '../../components/Section/Section';
import Card from '../../components/Card/Card';
import GlobalCallout from '../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Accordion from '../../components/Accordion/Accordion';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import { mappedNAICS } from '../../data/MappedNAICS';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="NAICS Codes | General Genomics"
        description="Explore General Genomics’ industry classifications and capabilities. Learn how its services span diverse sectors, supporting government and commercial clients."
      />
      <Hero
        helper="Innovation"
        headline="Comprehensive NAICS services for government and commercial sectors"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Streamline Your Industry Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Discover how General Genomics&apos; comprehensive expertise across
          industry classifications can empower your organization to achieve its
          goals efficiently. Our detailed NAICS codes outline the breadth of our
          capabilities, ensuring compliance and alignment with your
          sector&apos;s unique needs. By exploring our diverse services,
          you&apos;ll gain insights into tailored solutions that drive impactful
          results. Contact us today to see how our proven track record can
          support your mission and unlock new opportunities for success.
        </p>
        <Image
          path="/images/library/military-mechanic-aircraft-group-tablet.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/lab-technicians-group-laptop-7.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Why Choose Us</p>
        <h2 className="action-on-view action-fade-in">
          Unlock Industry-Specific Expertise
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Comprehensive NAICS Alignment:</strong> Ensure compliance
            and align seamlessly with government and commercial sector
            requirements.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Tailored Solutions:</strong> Leverage our extensive
            capabilities to create custom strategies designed to meet your
            organization’s specific objectives and challenges.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proven Track Record:</strong> Explore real-world examples of
            successful projects across industries to understand our impact and
            expertise.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Cross-Industry Applications:</strong> Benefit from our
            experience in applying solutions across diverse sectors, ensuring
            versatility and innovation.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Seamless Integration:</strong> Connect NAICS codes with
            procurement frameworks, industries, and custom solutions for
            streamlined collaboration and results.
          </p>
        </ListItem>
      </ContentSplit>

      {/* Biosecurity and Surveillance Section */}
      <Section>
        <ContentSplit
          image="/images/library/office-military-2.webp"
          position="left"
          className="valign-top"
        >
          <p className="helper action-on-view action-fade-in">
            Protecting National Security
          </p>
          <h2 className="action-on-view action-fade-in">
            Advanced Threat Detection and Response Systems
          </h2>
          <p className="action-on-view action-fade-in">
            General Genomics delivers comprehensive biosecurity solutions
            through AI-powered surveillance, threat detection, and response
            capabilities for military and civilian protection.
          </p>
          <div className="accordion-wrapper action-on-view action-fade-in">
            <Accordion
              headline={
                <>
                  Biosurveillance and Biosecurity
                  <em>
                    NAICS Code 541690: Other Scientific and Technical Consulting
                    Services
                  </em>
                </>
              }
            >
              <p>
                General Genomics enhances national security through AI/ML
                algorithms for comprehensive biosurveillance and biosecurity.
                Our analysis of clinical and genomic data enables proactive
                identification and assessment of potential biological threats,
                ensuring timely interventions and robust response strategies.
              </p>
            </Accordion>
            <Accordion
              headline={
                <>
                  Epidemiological Modeling and Simulation
                  <em>NAICS Code 541519: Other Computer Related Services</em>
                </>
              }
            >
              <p>
                Our AI/ML models offer precise epidemiological forecasts by
                simulating the spread of infectious diseases. This capability
                supports effective containment strategies and enhances the
                protection of military and civilian populations from potential
                outbreaks.
              </p>
              <p>
                We have three patents pending that are centered on innovative
                machine learning technology designed to identify illnesses and
                diseases across all living organisms, from bacteria to humans.
              </p>
            </Accordion>
            <Accordion
              headline={
                <>
                  Biological Threat Detection and Response
                  <em>
                    NAICS Code 541690: Other Scientific and Technical Consulting
                    Services
                  </em>
                </>
              }
            >
              <p>
                General Genomics excels in detecting and responding to
                biological threats through advanced AI/ML algorithms. By
                analyzing real-time data from our data lake, we identify and
                assess pathogens and toxins, ensuring proactive and effective
                responses to safeguard public health and national security.
              </p>
            </Accordion>
          </div>
        </ContentSplit>
      </Section>

      {/* Data and Analytics Section */}
      <Section>
        <ContentSplit
          image="/images/library/lab-group-research.webp"
          position="right"
          className="valign-top"
        >
          <p className="helper action-on-view action-fade-in">
            Actionable Data Insights
          </p>
          <h2 className="action-on-view action-fade-in">
            Transforming Biological Data Into Strategic Advantage
          </h2>
          <p className="action-on-view action-fade-in">
            General Genomics&apos;s advanced analytics platform processes
            complex biological datasets to deliver critical insights for
            government agencies and healthcare operations.
          </p>
          <div className="accordion-wrapper action-on-view action-fade-in">
            <Accordion
              headline={
                <>
                  Bioinformatics and Big Data Analytics
                  <em>NAICS Code 541512: Computer Systems Design Services</em>
                </>
              }
            >
              <p>
                General Genomics leverages bioinformatics and big data analytics
                to process and interpret vast biological datasets. Our
                capabilities support critical decisions across DOD, HHS, DHS,
                Intel Community, USDA, FDA, and NASA, enabling rapid and
                informed responses to emerging threats and challenges.
              </p>
            </Accordion>
            <Accordion
              headline={
                <>
                  Life Sciences and Clinical Research
                  <em>
                    NAICS Code 541715: Research and Development in the Physical,
                    Engineering, and Life Sciences
                  </em>
                </>
              }
            >
              <p>
                We drive innovation in life sciences and clinical research using
                our extensive data lake. General Genomics develops solutions to
                complex medical challenges, improving the health and performance
                of military personnel and ensuring resilience in joint forces.
              </p>
              <p>
                We have three patents pending that are centered on innovative
                machine learning technology designed to identify illnesses and
                diseases across all living organisms, from bacteria to humans.
              </p>
            </Accordion>
          </div>
        </ContentSplit>
      </Section>

      {/* Security and Identity Management Section */}
      <Section>
        <ContentSplit
          image="/images/library/office-military-3.webp"
          position="left"
          className="valign-top"
        >
          <p className="helper action-on-view action-fade-in">
            Securing Critical Infrastructure
          </p>
          <h2 className="action-on-view action-fade-in">
            Next-Generation Identity and Cybersecurity Solutions
          </h2>
          <p className="action-on-view action-fade-in">
            General Genomics&apos;s AI-powered security systems protect
            sensitive installations through advanced biometrics and real-time
            threat detection.
          </p>
          <div className="accordion-wrapper action-on-view action-fade-in">
            <Accordion
              headline={
                <>
                  Biometrics and Identity Management
                  <em>NAICS Code 561621: Security Systems Services</em>
                </>
              }
            >
              <p>
                General Genomics provides cutting-edge biometrics and identity
                management solutions, leveraging advanced AI/ML technologies.
                Our systems offer robust biometric identification and
                verification, securing sensitive installations and ensuring
                personnel integrity, crucial for deterring both foreign and
                domestic threats.
              </p>
            </Accordion>
            <Accordion
              headline={
                <>
                  Artificial Intelligence for Cybersecurity
                  <em>NAICS Code 541519: Other Computer Related Services</em>
                </>
              }
            >
              <p>
                We enhance cybersecurity with AI/ML technologies designed to
                detect and mitigate cyber threats in real-time. General
                Genomics&apos;s solutions protect sensitive data and critical
                infrastructure, ensuring the security and integrity of national
                defense operations.
              </p>
            </Accordion>
          </div>
        </ContentSplit>
      </Section>

      {/* Logistics and Maintenance Section */}
      <Section>
        <ContentSplit
          image="/images/library/military-mechanic-aircraft.webp"
          position="right"
          className="valign-top"
        >
          <p className="helper action-on-view action-fade-in">
            Optimizing Military Operations
          </p>
          <h2 className="action-on-view action-fade-in">
            Smart Supply Chain and Maintenance Management
          </h2>
          <p className="action-on-view action-fade-in">
            General Genomics streamlines military logistics through AI-driven
            supply chain management, predictive maintenance, and asset tracking
            solutions.
          </p>
          <div className="accordion-wrapper action-on-view action-fade-in">
            <Accordion
              headline={
                <>
                  Supply Chain Risk Management (SCRM)
                  <em>
                    NAICS Code 541614: Process, Physical Distribution, and
                    Logistics Consulting Services
                  </em>
                </>
              }
            >
              <p>
                General Genomics enhances logistics through AI/ML-driven supply
                chain risk management. Our data-driven insights predict
                potential disruptions, ensuring the continuous flow of critical
                resources and supporting lifecycle maintenance workflows,
                thereby optimizing military readiness.
              </p>
            </Accordion>
            <Accordion
              headline={
                <>
                  Enterprise-Fleet Level Maintenance Optimization
                  <em>
                    NAICS Code 541614: Process, Physical Distribution, and
                    Logistics Consulting Services
                  </em>
                </>
              }
            >
              <p>
                We optimize enterprise-fleet maintenance using AI/ML
                technologies. By leveraging predictive analytics from our
                extensive data lake, General Genomics improves maintenance
                scheduling and extends asset lifecycles, ensuring that military
                fleets and equipment remain in top operational condition.
              </p>
            </Accordion>
            <Accordion
              headline={
                <>
                  Medical Logistics and Asset Tracking
                  <em>
                    NAICS Code 541614: Process, Physical Distribution, and
                    Logistics Consulting Services
                  </em>
                </>
              }
            >
              <p>
                General Genomics supports military readiness with AI/ML
                solutions for medical logistics and asset tracking. Our
                technology optimizes the distribution and management of medical
                supplies and equipment, ensuring timely access and enhancing
                operational effectiveness.
              </p>
            </Accordion>
          </div>
        </ContentSplit>
      </Section>

      {/* Human Performance Section */}
      <Section>
        <ContentSplit
          image="/images/library/gym-military-man-2.webp"
          position="left"
          className="valign-top"
        >
          <p className="helper action-on-view action-fade-in">
            Enhancing Military Readiness
          </p>
          <h2 className="action-on-view action-fade-in">
            Maximizing Personnel Performance Through Advanced Technology
          </h2>
          <p className="action-on-view action-fade-in">
            General Genomics&apos;s AI solutions optimize physical, cognitive,
            and psychological capabilities of military personnel to enhance
            mission effectiveness.
          </p>
          <div className="accordion-wrapper action-on-view action-fade-in">
            <Accordion
              headline={
                <>
                  Human Performance Optimization
                  <em>
                    NAICS Code 541720: Research and Development in the Social
                    Sciences and Humanities
                  </em>
                </>
              }
            >
              <p>
                At General Genomics, Inc. (GGI), we are dedicated to maximizing
                human performance for military personnel through advanced
                AI/ML-driven solutions. Our approach integrates comprehensive
                data analysis and cutting-edge technologies to enhance the
                physical, cognitive, and psychological readiness of service
                members.
              </p>
              <p>
                General Genomics&apos;s commitment to human performance
                optimization ensures that military personnel are equipped to
                excel in their missions, protecting their well-being while
                enhancing their operational effectiveness. Our solutions are
                designed to support and elevate the capabilities of service
                members, enabling them to perform at their best and achieve
                their mission objectives with confidence and efficiency.
              </p>
            </Accordion>
          </div>
        </ContentSplit>
      </Section>

      {/* Emerging Threats Section */}
      <Section>
        <ContentSplit
          image="/images/library/drones.webp"
          position="right"
          className="valign-top"
        >
          <p className="helper action-on-view action-fade-in">
            Counter-Drone Defense Systems
          </p>
          <h2 className="action-on-view action-fade-in">
            Protecting Assets from Advanced Aerial Threats
          </h2>
          <p className="action-on-view action-fade-in">
            General Genomics&apos;s counter-drone technologies detect and
            neutralize aerial threats, safeguarding critical infrastructure and
            personnel.
          </p>
          <div className="accordion-wrapper action-on-view action-fade-in">
            <Accordion
              headline={
                <>
                  Counter-Drone Capabilities
                  <em>
                    NAICS Code 541715: Research and Development in the Physical,
                    Engineering, and Life Sciences
                  </em>
                </>
              }
            >
              <p>
                General Genomics addresses emerging threats with advanced
                AI/ML-driven counter-drone technologies. Our systems are
                designed to detect, track, and neutralize hostile drones,
                ensuring the protection of critical assets and personnel from
                aerial threats.
              </p>
            </Accordion>
          </div>
        </ContentSplit>
      </Section>

      {/* Workforce Development Section */}
      <Section>
        <ContentSplit
          image="/images/library/command-center-group-2.webp"
          position="left"
          className="valign-top"
        >
          <p className="helper action-on-view action-fade-in">
            Future-Ready Training Solutions
          </p>
          <h2 className="action-on-view action-fade-in">
            Advanced AI and AGI Workforce Development
          </h2>
          <p className="action-on-view action-fade-in">
            General Genomics provides comprehensive training in AI/AGI
            technologies, preparing professionals for next-generation enterprise
            solutions.
          </p>
          <div className="accordion-wrapper action-on-view action-fade-in">
            <Accordion
              headline={
                <>
                  AI and AGI Technical Training and Development
                  <em>
                    NAICS Code 611430: Professional and Management Development
                    Training
                  </em>
                </>
              }
            >
              <p>
                General Genomics equips professionals with advanced training in
                AI and Artificial General Intelligence (AGI) technologies. Our
                programs focus on designing and implementing large language
                models (LLMs) and integrating these technologies into
                large-scale enterprise workflows. This training enhances
                productivity and efficiency, ensuring that the workforce remains
                adept in evolving technological landscapes.
              </p>
            </Accordion>
            <Accordion
              headline={
                <>
                  AI-Enhanced Workflow Optimization
                  <em>NAICS Code 541330: Engineering Services</em>
                </>
              }
            >
              <p>
                We drive workflow efficiency with AI-enhanced optimization
                training. General Genomics provides expertise in applying AI and
                AGI technologies to streamline processes and automate tasks,
                improving overall productivity and adapting to technological
                advancements.
              </p>
            </Accordion>
          </div>
        </ContentSplit>
      </Section>

      <Section>
        <ContentSplit
          image="/images/library/office-military-laptop.webp"
          position="right"
          className="valign-top"
        >
          <p className="helper action-on-view action-fade-in">Mapped NAICS</p>
          <h2 className="action-on-view action-fade-in">
            Mapped NAICS for Targeted AI/ML Solutions
          </h2>
          <p className="action-on-view action-fade-in">
            Leveraging mapped NAICS codes to deliver AI/ML-driven innovations
            tailored to meet industry-specific challenges and enhance
            operational outcomes.
          </p>
          <div className="list-datarow-wrapper">
            {mappedNAICS.map((naics) => (
              <ListItem
                key={naics.code}
                listtype="datarow"
                bullet={naics.code}
                className="custom-list-item"
              >
                {naics.description}
              </ListItem>
            ))}
          </div>
        </ContentSplit>
      </Section>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More Genomics Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Discover how our tailored solutions and sector expertise connect with
          various procurement frameworks, industries, and data solutions.
        </p>
        <div className="card-group">
          <Card
            icon="business"
            headline="Industries"
            subhead="See how NAICS classifications align with our genomics expertise across diverse sectors."
            btnURL="/industries"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="data_usage"
            headline="Custom Data Solutions"
            subhead="Explore tailored data solutions that enhance our genomics services within NAICS-defined areas."
            btnURL="/industries/commercial/custom-informatics"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/base-aircrafts.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about NAICS Codes and Capabilities
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn how General Genomics&apos; NAICS codes define industry expertise
          and services for government and commercial clients.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What are NAICS codes, and why are they important for my business?"
          >
            NAICS codes are industry classifications used by{' '}
            <Link to="/industries/government">government</Link> and{' '}
            <Link to="/industries/commercial">commercial</Link> entities to
            identify the scope of services offered by companies. They help
            clients understand General Genomics&apos; diverse capabilities
            across various sectors, ensuring compliance and expertise in
            specific industries.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How do the NAICS codes reflect General Genomics' service offerings?"
          >
            The NAICS codes on this page categorize General Genomics&apos;
            expertise into various sectors, detailing the company&apos;s
            capabilities for <Link to="/industries/government">government</Link>{' '}
            and <Link to="/industries/commercial">commercial</Link> clients.
            These codes help highlight tailored solutions for specific industry
            needs, ensuring targeted and effective services.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can you provide examples of projects General Genomics has completed under specific NAICS codes?"
          >
            Yes, General Genomics has worked on numerous projects across
            different NAICS classifications, demonstrating expertise in sectors
            like healthcare, data solutions, and research. These examples
            highlight the company&apos;s capacity to deliver tailored solutions
            based on client needs.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How do NAICS codes align with General Genomics' procurement frameworks?"
          >
            General Genomics&apos; NAICS codes are linked to various contract
            vehicles, helping clients navigate procurement processes. These
            codes ensure that the company is equipped to meet specific industry
            requirements, offering seamless engagement with{' '}
            <Link to="/industries/government">government</Link> and{' '}
            <Link to="/industries/commercial">commercial</Link> entities.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I learn more about how General Genomics can help with my specific needs?"
          >
            Explore our capabilities further by requesting a demo or reaching
            out to a representative. We can provide more detailed insights into
            how our NAICS-based services align with your business needs and
            industry requirements.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
